import { useQuery } from "@tanstack/solid-query";
import type { RunSummary } from "contracts/api/crawl/run-read.responses";
import { CRAWL_RUN_STATUS, isActiveCrawlRunStatus } from "contracts/enums/core";
import { createMemo, For, Show } from "solid-js";
import { SCAN_SECTION_KEY } from "../../enums/scan";
import { api } from "../../store/api";
import { EmptyState } from "../../ui/EmptyState";
import { relativeAge } from "../../ui/format";
import { PipelineStepper } from "../../ui/PipelineStepper";
import { ScanRouteSurface } from "../scan/ScanRouteChrome";
import { ScanRouteHeader } from "../scan/ScanRouteHeader";
import { ScanShell } from "../scan/ScanShell";
import { overviewScanAvailability } from "../scan/scanAvailability";
import { createWebsiteStore } from "../website/store";
import {
  runDuration,
  runStatusClass,
  runSummaryStatusLabel,
} from "./runPresentation";
import { runPipelineSteps } from "./runStagePresentation";

export function RunsList(props: { websiteId: string }) {
  const context = createWebsiteStore(props.websiteId);
  const availability = createMemo(() => {
    const data = context.data();
    return data ? overviewScanAvailability(data) : null;
  });
  const runs = useQuery(() => ({
    queryKey: ["runs", props.websiteId],
    queryFn: () => api.listRuns(props.websiteId),
  }));

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.RUNS}>
      <ScanRouteSurface>
        <ScanRouteHeader
          websiteId={props.websiteId}
          section="Runs"
          title="Runs"
          subtitle="Review scan coverage, failures and the completed run behind the current score."
        />
        <Show when={availability()} keyed>
          {(state) => (
            <div class="signal-scan-availability" data-state={state.key}>
              <strong>{state.label}</strong>
              <span>{state.detail}</span>
            </div>
          )}
        </Show>
        <Show
          when={runs.data}
          keyed
          fallback={<div class="loading signal-loading">Loading runs...</div>}
        >
          {(data) => (
            <Show
              when={data.runs.length > 0}
              fallback={
                <EmptyState
                  title="No scans have run yet"
                  hint="Use “Run scan” in the top bar to start the first crawl."
                />
              }
            >
              <div class="signal-runs-table-wrap">
                <table class="tbl tbl-link">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Started</th>
                      <th>Duration</th>
                      <th>Pages</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={data.runs}>
                      {(run) => (
                        <RunRow websiteId={props.websiteId} run={run} />
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          )}
        </Show>
      </ScanRouteSurface>
    </ScanShell>
  );
}

function RunRow(props: { websiteId: string; run: RunSummary }) {
  const coverageWarning = () =>
    props.run.status === CRAWL_RUN_STATUS.COMPLETED &&
    (props.run.pageCounts.failed > 0 || props.run.pageCounts.skipped > 0);
  return (
    <tr>
      <td>
        <a
          class="scan-table-link"
          href={`/w/${props.websiteId}/runs/${props.run.runId}`}
          aria-label={`Open ${runSummaryStatusLabel(props.run)} scan from ${props.run.startedAt.toLocaleString()}`}
        >
          <span class={`badge ${runStatusClass(props.run)}`}>
            {runSummaryStatusLabel(props.run)}
          </span>
        </a>
      </td>
      <td title={props.run.startedAt.toLocaleString()}>
        {relativeAge(props.run.startedAt)}
      </td>
      <td>{runDuration(props.run)}</td>
      <td>
        <Show
          when={isActiveCrawlRunStatus(props.run.status)}
          fallback={`${props.run.pageCounts.scraped} completed`}
        >
          <PipelineStepper steps={runPipelineSteps(props.run)} compact />
        </Show>
        <Show when={coverageWarning()}>
          <span
            class="badge badge-sev sev-medium"
            title={`${props.run.pageCounts.failed} failed and ${props.run.pageCounts.skipped} skipped`}
          >
            Completed with partial coverage
          </span>
        </Show>
      </td>
    </tr>
  );
}
